import {match} from 'ts-pattern';

import {mergeAll} from 'ramda';
import {isString} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import {catchUnhandledDataGridActions, DmsDataGrid, openActivityChangeLog} from '@dms/shared';

import {TestIdProps} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

const GRID_CODE = 'task-audit-log';
const PREDEFINED_COLUMNS = ['commandId'] as const;

interface TaskActivityLogProps extends TestIdProps {
  taskId: string;
}

/**
 * A task's history: who changed it and when, and — when a change was made by an
 * automation rule — the rule's name as the actor (T20-136397, RQ_18). The grid
 * requires exactly one `record`; a reader who may not read the task gets no rows,
 * which the backend decides. A row opens that command's change detail.
 */
export function TaskActivityLog(props: TaskActivityLogProps) {
  const queryModifier = (filter: QueryFilterObject) => mergeAll([filter, {record: props.taskId}]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = (data) => {
    const commandId = data.getColumnData('commandId')?.value;

    match(data.actionKey)
      .with('detail', () =>
        isString(commandId)
          ? openActivityChangeLog({
              resourceId: EntityResourceIds.task,
              commandId,
              recordId: props.taskId,
            })
          : undefined
      )
      .otherwise(() => catchUnhandledDataGridActions(data.actionKey));
  };

  return (
    <DmsDataGrid
      gridCode={GRID_CODE}
      predefinedColumns={PREDEFINED_COLUMNS}
      actionCallback={actionCallback}
      queryModifier={queryModifier}
      autoHeight
      data-testid={props['data-testid']}
    />
  );
}
